/* ═════════════════════════ AdMob (Community plugin) ═══════════════════════
   Uses @capacitor-community/admob which is stable and builds cleanly against
   Capacitor 8 + modern AdMob SDK.

   We show a **MEDIUM_RECTANGLE (300×250)** banner positioned at CENTER of the
   screen — this closely mirrors the "dragon slot" placement and looks like
   an inline native ad. Trade-off: the banner overlays the WebView (native
   layer), so on scroll it lags briefly. This is a known Capacitor limitation
   for hybrid apps and is acceptable for our use case.
   ─────────────────────────────────────────────────────────────────────────── */

import { AdMob as AdMobPlugin } from '@capacitor-community/admob';

export const ADMOB_APP_ID         = process.env.REACT_APP_ADMOB_APP_ID || '';
// The ad unit we request. Since we're serving a MEDIUM_RECTANGLE (banner-family
// request), we must use a Banner-type ad unit — Native Advanced units don't
// serve banner responses.
export const ADMOB_AD_UNIT_ID     = process.env.REACT_APP_ADMOB_BANNER_UNIT_ID
                                  || '';

// Google's official TEST banner ID — used only if REACT_APP_ADMOB_NATIVE_UNIT_ID
// isn't set (dev/preview safety net).
export const ADMOB_TEST_BANNER_ID = 'ca-app-pub-3940256099942544/6300689190';

const isNativePlatform = () => {
  try {
    // eslint-disable-next-line global-require
    const { Capacitor } = require('@capacitor/core');
    return Capacitor && typeof Capacitor.isNativePlatform === 'function'
      ? Capacitor.isNativePlatform()
      : false;
  } catch (_e) { return false; }
};

let _plugin = null;
let _initPromise = null;

// Bundled import — the old `webpackIgnore` dynamic import asked the WebView
// to resolve a bare npm specifier at runtime, which always failed, so the
// plugin never loaded and no ad was ever requested.
const loadPlugin = async () => {
  if (!_plugin) _plugin = AdMobPlugin || null;
  return _plugin;
};

const initAdMob = async () => {
  if (_initPromise) return _initPromise;
  _initPromise = (async () => {
    if (!isNativePlatform()) return false;
    const AdMob = await loadPlugin();
    if (!AdMob) return false;
    try {
      await AdMob.initialize({ initializeForTesting: false });
      return true;
    } catch (e) {
      console.warn('[AdMob] initialize failed', e);
      return false;
    }
  })();
  return _initPromise;
};

/**
 * Show a 300×250 MEDIUM_RECTANGLE banner centred over the dragon slot area.
 * Safe no-op on web.
 */
const showAdMobBanner = async ({ useTestAd = false, top = null } = {}) => {
  if (!isNativePlatform()) return { shown: false, reason: 'web' };
  const ready = await initAdMob();
  if (!ready) return { shown: false, reason: 'init-failed' };
  const AdMob = await loadPlugin();
  if (!AdMob) return { shown: false, reason: 'no-plugin' };
  const adId = useTestAd ? ADMOB_TEST_BANNER_ID : (ADMOB_AD_UNIT_ID || ADMOB_TEST_BANNER_ID);
  try {
    await AdMob.showBanner({
      adId,
      adSize: 'MEDIUM_RECTANGLE',   // 300×250 — fills the gold ad frame
      // `top` = the frame's top edge in WebView px (1 css px = 1 dp), so the
      // banner lands exactly inside the rectangular frame on the Inscribe tab.
      position: top != null ? 'TOP_CENTER' : 'BOTTOM_CENTER',
      margin: top != null ? Math.max(1, Math.round(top)) : 72,
      isTesting: !ADMOB_AD_UNIT_ID,
    });
    return { shown: true };
  } catch (e) {
    console.warn('[AdMob] showBanner failed', e);
    return { shown: false, reason: 'error', error: String(e) };
  }
};

const hideAdMobBanner = async () => {
  if (!isNativePlatform()) return;
  const AdMob = await loadPlugin();
  try { await AdMob?.hideBanner?.(); } catch (_e) { /* best-effort */ }
};

/* Fully destroys the banner view so the next show starts fresh (no stacking). */
const removeAdMobBanner = async () => {
  if (!isNativePlatform()) return;
  const AdMob = await loadPlugin();
  try { await AdMob?.removeBanner?.(); } catch (_e) { /* best-effort */ }
};

export const isAdMobSupported = () => isNativePlatform();
export { initAdMob, showAdMobBanner, hideAdMobBanner, removeAdMobBanner };
